import "chart.js/auto";
import { Chart } from "react-chartjs-2";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

const COLORS = {
  target: "#26BF00",
  current: "#2E93fA",
  integrated: "#A53838",
  forecast: "#DC6CFE",
  geyser: "#FDB940",
};

const labelColor = "#b0b8c4";
const gridColor = "#252b38";
const darkBg = "#161b27";

function hex2rgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function formatLabel(isoString) {
  const d = new Date(isoString);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      position: "top",
      labels: { color: labelColor, usePointStyle: true, pointStyleWidth: 10, boxHeight: 8, font: { size: 11 } },
    },
    tooltip: {
      backgroundColor: "#1a2035",
      borderColor: "#2a3550",
      borderWidth: 1,
      titleColor: "#7a8aaa",
      bodyColor: "#e0e6f0",
      padding: 12,
      callbacks: {
        label: (ctx) =>
          ctx.parsed.y !== null ? ` ${ctx.dataset.label}: ${ctx.parsed.y.toFixed(3)} MVA` : null,
      },
    },
  },
  scales: {
    x: {
      type: "category",
      grid: { color: gridColor },
      ticks: { color: labelColor, font: { size: 10 }, maxTicksLimit: 10 },
      border: { color: gridColor },
    },
    y: {
      grid: { color: gridColor },
      ticks: { color: labelColor, font: { size: 10 }, callback: (v) => v.toFixed(2) },
      border: { color: gridColor },
      title: { display: true, text: "MVA", color: labelColor, font: { size: 11 } },
    },
  },
};

function EmptyState() {
  return (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: "#5a6070" }}>No data available</Typography>
    </Box>
  );
}

export default function ChartJsTemplate({ microServiceGraphData, targetDataNow, microserviceForcastState }) {
  const hasData = microServiceGraphData.length > 0;
  const labels = microServiceGraphData.map((item) => formatLabel(item.interval));

  const demandData = {
    labels,
    datasets: [
      {
        type: "line",
        label: "Target Demand",
        data: microServiceGraphData.map(() =>
          targetDataNow?.target ? +(targetDataNow.target / 1000000).toFixed(4) : null
        ),
        borderColor: COLORS.target,
        backgroundColor: hex2rgba(COLORS.target, 0.15),
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        tension: 0.3,
        fill: true,
        spanGaps: true,
        order: 1,
      },
      {
        type: "line",
        label: "Current Demand",
        data: microServiceGraphData.map((item) =>
          item.current_demand ? +(item.current_demand / 1000000).toFixed(4) : null
        ),
        borderColor: COLORS.current,
        backgroundColor: hex2rgba(COLORS.current, 0.25),
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.4,
        fill: true,
        spanGaps: true,
        order: 2,
      },
      {
        type: "line",
        label: "Integrated Demand",
        data: microServiceGraphData.map((item) =>
          item.latest_integrated_demand ? +(item.latest_integrated_demand / 1000000).toFixed(4) : null
        ),
        borderColor: COLORS.integrated,
        backgroundColor: hex2rgba(COLORS.integrated, 0.2),
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3,
        fill: true,
        spanGaps: true,
        order: 3,
      },
      {
        type: "line",
        label: "Forecast",
        data: microServiceGraphData.map(() =>
          microserviceForcastState?.[0]?.forecast
            ? +(microserviceForcastState[0].forecast / 1000000).toFixed(4)
            : null
        ),
        borderColor: COLORS.forecast,
        backgroundColor: "transparent",
        borderWidth: 2,
        borderDash: [4, 4],
        pointRadius: 0,
        spanGaps: true,
        order: 0,
      },
    ],
  };

  const geyserData = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "Geyser Demand",
        data: microServiceGraphData.map((item) =>
          item.geyser_demand ? +(item.geyser_demand / 1000000).toFixed(4) : null
        ),
        backgroundColor: hex2rgba(COLORS.geyser, 0.7),
        borderColor: COLORS.geyser,
        borderWidth: 1,
        borderRadius: 2,
      },
    ],
  };

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ color: labelColor, mb: 1, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>
          Demand Overview — MVA
        </Typography>
        <Box sx={{ bgcolor: darkBg, borderRadius: 2, p: 2, height: 380 }}>
          {hasData ? <Chart type="line" data={demandData} options={baseOptions} /> : <EmptyState />}
        </Box>
      </Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ color: labelColor, mb: 1, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>
          Geyser (Tjommie) Demand — MVA
        </Typography>
        <Box sx={{ bgcolor: darkBg, borderRadius: 2, p: 2, height: 380 }}>
          {hasData ? <Chart type="bar" data={geyserData} options={baseOptions} /> : <EmptyState />}
        </Box>
      </Box>
    </>
  );
}
